import type { StatusTone } from '~/components/ui';
import type { Badge, Operator, RequestStatus } from '~/components/pages/feature-flags/types';

// Ported from SRC lib/{constants,evaluate,status,updates-shared,requests-shared,attribute-keys,sdk-examples}.ts.
// Value lists live here; the types they are checked against come from sdk/lib through pages/feature-flags/types.

export const OPERATORS: readonly Operator[] = ['in', 'notIn', 'endsWith'];
export const OPERATOR_LABELS: Record<Operator, string> = {
  in: 'is one of',
  notIn: 'is not one of',
  endsWith: 'ends with',
};

export const FLAG_KEY_RE = /^[a-z0-9][a-z0-9-]{1,62}$/;
export const ATTR_KEY_RE = /^[a-zA-Z][a-zA-Z0-9_]{0,39}$/;
export const ENV_KEY_RE = /^[a-z][a-z0-9-]{0,30}$/;
export const PRODUCT_KEY_RE = /^[a-z0-9][a-z0-9-]{0,127}$/;

/** The six identity attributes every SDK context carries; they cannot be deleted. */
export const REQUIRED_ATTRIBUTE_KEYS = [
  'userId',
  'product',
  'customerId',
  'name',
  'email',
  'phone',
] as const;
export const isRequiredAttribute = (key: string): boolean =>
  REQUIRED_ATTRIBUTE_KEYS.some((required) => required === key);

/** `list`: the app sends an array (e.g. roles); a rule matches when any element does. */
export const ATTRIBUTE_TYPES = ['string', 'enum', 'boolean', 'list'] as const;

// ---- Flag status (per environment) ----
export type FlagStatus = 'targeted' | 'on' | 'off' | 'scheduled' | 'ended' | 'disabled';

export const FLAG_STATUS_LABELS: Record<FlagStatus, string> = {
  targeted: 'Targeted',
  on: 'On for everyone',
  off: 'Off for everyone',
  scheduled: 'Scheduled',
  ended: 'Ended',
  disabled: 'Disabled',
};

/** Status shown as a `StatusChip`: the tone carries the colour, never a colour string. */
export const FLAG_STATUS_TONES: Record<FlagStatus, StatusTone> = {
  targeted: 'brand',
  on: 'success',
  off: 'neutral',
  scheduled: 'info',
  ended: 'neutral',
  disabled: 'neutral',
};

/** One environment's status; the schedule (flags and product updates) comes before what it serves. */
export const flagStatus = (
  c: {
    enabled: boolean;
    rules: unknown[];
    defaultValue: boolean;
    startsAt?: string | null;
    endsAt?: string | null;
  },
  now = new Date(),
): FlagStatus => {
  if (!c.enabled) return 'disabled';
  if (c.endsAt && new Date(c.endsAt) <= now) return 'ended';
  if (c.startsAt && new Date(c.startsAt) > now) return 'scheduled';
  if (c.rules.length > 0) return 'targeted';
  return c.defaultValue ? 'on' : 'off';
};

// ---- Product updates ----
export const BADGES: readonly Badge[] = ['devOnly', 'preview', 'beta', 'new', 'endOfLife'];
export const BADGE_LABELS: Record<Badge, string> = {
  devOnly: 'Dev only',
  preview: 'Preview',
  beta: 'Beta',
  new: 'New',
  endOfLife: 'End-of-Life',
};
export const BADGE_HELP: Record<Badge, string> = {
  devOnly: 'Built internally and being tested before any user sees it.',
  preview: 'Available in production to BAY staff, ideally in a demo account.',
  beta: 'Users should see this as a beta.',
  new: 'Something new on the platform.',
  endOfLife: 'A feature, setting or page we plan to retire.',
};
export const BADGE_TONES: Record<Badge, StatusTone> = {
  devOnly: 'neutral',
  preview: 'info',
  beta: 'warning',
  new: 'success',
  endOfLife: 'error',
};
export const DEFAULT_LINK_LABEL = 'Take me there!';
export const DEFAULT_LEARN_MORE_LABEL = 'Learn more';

// ---- Feature requests ----
export const REQUEST_STATUSES: readonly RequestStatus[] = [
  'submitted',
  'approved',
  'under-review',
  'planned',
  'in-progress',
  'delivered',
  'not-planned',
];
export const REQUEST_STATUS_LABELS: Record<RequestStatus, string> = {
  submitted: 'Submitted',
  approved: 'Approved',
  'under-review': 'Under review',
  planned: 'Planned',
  'in-progress': 'In progress',
  delivered: 'Delivered',
  'not-planned': 'Not planned',
};
export const REQUEST_STATUS_TONES: Record<RequestStatus, StatusTone> = {
  submitted: 'neutral',
  approved: 'info',
  'under-review': 'warning',
  planned: 'brand',
  'in-progress': 'brand',
  delivered: 'success',
  'not-planned': 'neutral',
};
export const REQUEST_ORDERS = [
  { id: 'latest', label: 'Latest' },
  { id: 'votes', label: 'Trending' },
] as const;
export const REQUEST_PAGE_SIZE = 20;

// ---- Known users ----
export const USERS_PAGE_SIZE = 50;

// ---- SDK examples (SRC lib/sdk-examples.ts, paths rewritten to sdk/…; the opt-in choices calls are gone) ----
export interface SdkExample {
  id: string;
  label: string;
  filename: string;
  setup: string;
  code: string;
}

export const SDK_EXAMPLES: readonly SdkExample[] = [
  {
    id: 'node',
    label: 'Node / TypeScript',
    filename: 'flags.ts',
    setup:
      'Copy sdk/node/flags-client.ts and the sdk/lib folder (evaluate.ts, updates-shared.ts, requests-shared.ts) into your app, keeping lib/ beside node/. Create one client per backend process.',
    code: `import { FlagsClient } from '~/components/Constants/sdk/node/flags-client';

const flags = new FlagsClient({
  baseUrl: process.env.FLAGS_URL!,
  sdkKey: process.env.FLAGS_SDK_KEY!,
});

// Build this context from your authenticated user.
const context = {
  userId: 'user-123',
  product: 'your-product-key',
  customerId: 'customer-456',
  name: 'Pat Smith',
  email: 'pat@example.com',
  phone: '+15550101234',
};

// At login and whenever profile details change:
await flags.identifyUser(context);

const enabled = await flags.isOn('your-flag-key', context, false);
const values = await flags.all(context);
const updates = await flags.productUpdates(context);

// In your authenticated product update toggle handler:
// await flags.setUpdateChoice(context, 'your-update-key', true);`,
  },
  {
    id: 'php',
    label: 'PHP',
    filename: 'flags.php',
    setup:
      'Copy sdk/php/FlagsClient.php into your app. Requires PHP 7.4+ with curl and mbstring. The client caches definitions and evaluates flags locally.',
    code: `<?php
require_once __DIR__ . '/sdk/php/FlagsClient.php';

$flags = new FlagsClient(
    getenv('FLAGS_URL'),
    getenv('FLAGS_SDK_KEY')
);

// Build this context from your authenticated user.
$context = [
    'userId' => 'user-123',
    'product' => 'your-product-key',
    'customerId' => 'customer-456',
    'name' => 'Pat Smith',
    'email' => 'pat@example.com',
    'phone' => '+15550101234',
];

// At login and whenever profile details change:
$flags->identifyUser($context);

$enabled = $flags->isOn('your-flag-key', $context, false);
$values = $flags->all($context);
$updates = $flags->productUpdates($context);

// In your authenticated product update toggle handler:
// $flags->setUpdateChoice($context, 'your-update-key', true);`,
  },
  {
    id: 'cfml',
    label: 'ColdFusion / Lucee',
    filename: 'Application.cfc + request (CFScript)',
    setup:
      'Copy sdk/cfml/FlagsClient.cfc into your app. Requires Adobe ColdFusion 2018+ or Lucee 5+. Initialize the client once in onApplicationStart(); run the context and evaluation code in your authenticated request.',
    code: `// Application.cfc: inside onApplicationStart()
application.flags = new sdk.cfml.FlagsClient(
    baseUrl = server.system.environment.FLAGS_URL,
    sdkKey = server.system.environment.FLAGS_SDK_KEY
);

// Authenticated request: use the current user's actual data.
context = {
    "userId": "user-123",
    "product": "your-product-key",
    "customerId": "customer-456",
    "name": "Pat Smith",
    "email": "pat@example.com",
    "phone": "+15550101234"
};

// At login and whenever profile details change:
application.flags.identifyUser(context);

enabled = application.flags.isOn("your-flag-key", context, false);
values = application.flags.allFlags(context);
updates = application.flags.productUpdates(context);

// In your authenticated product update toggle handler:
// application.flags.setUpdateChoice(context, "your-update-key", true);`,
  },
  {
    id: 'http',
    label: 'HTTP / cURL',
    filename: 'evaluate.sh',
    setup:
      'Call this from your backend. This endpoint returns evaluated feature values and syncs complete user context. For saved product update preferences, use one of the SDK clients.',
    code: `curl --fail-with-body "$FLAGS_URL/api/sdk/evaluate" \\
  -H "Authorization: Bearer $FLAGS_SDK_KEY" \\
  -H "Content-Type: application/json" \\
  --data '{
    "context": {
      "userId": "user-123",
      "product": "your-product-key",
      "customerId": "customer-456",
      "name": "Pat Smith",
      "email": "pat@example.com",
      "phone": "+15550101234"
    },
    "keys": ["your-flag-key"]
  }'`,
  },
  {
    id: 'react',
    label: 'React',
    filename: 'FeatureView.tsx',
    setup:
      'Copy sdk/react/FlagsProvider.tsx into your app. Your backend evaluates flags using all() in Node/PHP or allFlags() in ColdFusion, then passes only the resulting boolean map to React.',
    code: `'use client';
import { FlagsProvider, useFlag } from '~/components/Constants/sdk/react/FlagsProvider';

function FeatureContent() {
  const enabled = useFlag('your-flag-key', false);
  return enabled ? <div>New experience</div> : <div>Current experience</div>;
}

// Supply the evaluated values from your backend.
export function FeatureView({ flags }: { flags: Record<string, boolean> }) {
  return (
    <FlagsProvider flags={flags}>
      <FeatureContent />
    </FlagsProvider>
  );
}`,
  },
];
